import { useParams } from "react-router-dom";
import { h1Class, metaLabelClass } from "@groot/ui/typography";
import { cn } from "@groot/ui/lib/utils";
import { LoadingState } from "@groot/ui/loading-spinner";
import { useLightTheme } from "@/lib/useLightTheme";
import { RequestStatusBadge } from "@/components/status-badges";
import {
  useMomentRequestByPublicLink,
  usePublicUploadVideo,
  usePublicDeleteVideo,
} from "@/pages/moment-request/hooks/useMomentRequests";
import { toast } from "sonner";
import { useState, useRef } from "react";
import { Upload, Trash2, CheckCircle2, Loader2 } from "lucide-react";

export function PublicMomentUpload() {
  useLightTheme();
  const { publicLink } = useParams<{ publicLink: string }>();
  const { data: request, isLoading } = useMomentRequestByPublicLink(publicLink || "");
  const uploadVideo = usePublicUploadVideo(publicLink || "");
  const deleteVideo = usePublicDeleteVideo(publicLink || "");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !request) return;

    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        await uploadVideo.mutateAsync({
          file,
          contentType: file.type,
        });
      }
      toast.success("Uploaded", { description: "Video uploaded successfully" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Failed to upload video" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeVideo = async (videoId: number) => {
    if (!request) return;
    try {
      await deleteVideo.mutateAsync({ videoId });
      toast.success("Removed", { description: "Video removed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Failed to remove video" });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center">
        <LoadingState text="Loading…" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
        <div className="bg-card rounded-2xl shadow-sm p-8 max-w-sm w-full text-center">
          <p className="font-semibold text-foreground">Link not found</p>
          <p className="text-sm text-muted-foreground mt-1">
            This moment request link is invalid or has expired.
          </p>
        </div>
      </div>
    );
  }

  const isCompleted = request.status === "completed";
  const hasVideos = request.videos && request.videos.length > 0;
  const latestVideo = request.videos?.[0];
  const oldVideos = request.videos?.slice(1) ?? [];
  const viewUrl = `${window.location.origin}/public/moment-requests/view/${request.viewLink}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&color=000000&bgcolor=ffffff&data=${encodeURIComponent(viewUrl)}`;

  return (
    <div className="min-h-screen bg-muted/50 py-8 px-4">
      <div className="max-w-[480px] mx-auto bg-card rounded-2xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 flex items-center gap-2.5">
          <img src="/logo.png" alt="Riqah Design" className="h-12 w-auto" />
          <span className="text-sm font-semibold text-foreground">Riqah Design</span>
        </div>

        {/* Hero */}
        <div className="px-6 py-6 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Hello{request.customer?.name ? `, ${request.customer.name}` : ""}
          </p>
          <h1 className={cn(h1Class, "mt-0.5")}>Your Moments</h1>
          <div className="mt-3">
            <RequestStatusBadge
              status={isCompleted ? "completed" : "pending"}
              label={isCompleted ? "Completed" : "Pending"}
            />
          </div>
        </div>

        {/* QR Code */}
        <div className="px-6 py-4 border-t border-border flex flex-col items-center">
          <p className={cn(metaLabelClass, "mb-3")}>Share this page</p>
          <div className="border border-border rounded-xl p-2 bg-white">
            <img src={qrSrc} alt="QR Code" className="block w-[160px] h-[160px]" />
          </div>
        </div>

        {/* Upload zone — pending only */}
        {!isCompleted && (
          <div className="px-6 py-5 border-t border-border">
            <p className={cn(metaLabelClass, "mb-3")}>Upload Video</p>

            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            <button
              type="button"
              onClick={() => !isUploading && fileInputRef.current?.click()}
              disabled={isUploading}
              className="w-full border-2 border-dashed border-border rounded-xl py-8 flex flex-col items-center gap-2 hover:border-foreground/30 hover:bg-muted/50 disabled:pointer-events-none"
            >
              {isUploading ? (
                <>
                  <Loader2 className="size-6 text-muted-foreground animate-spin" />
                  <span className="text-sm font-medium text-foreground">Uploading…</span>
                </>
              ) : (
                <>
                  <Upload className="size-6 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Tap to upload video</span>
                  <span className="text-xs text-muted-foreground">
                    MP4, MOV, WEBM · up to 200MB
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Latest video player — auto-plays */}
        {hasVideos && latestVideo && (
          <div className="px-6 py-5 border-t border-border">
            <p className={cn(metaLabelClass, "mb-3")}>Latest Video</p>
            <div className="relative rounded-xl overflow-hidden bg-black">
              <video
                key={latestVideo.id}
                src={`/api/v1/public/moment-requests/${publicLink}/files/${latestVideo.id}`}
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="w-full aspect-video"
              />
              {!isCompleted && (
                <button
                  type="button"
                  onClick={() => removeVideo(latestVideo.id)}
                  disabled={deleteVideo.isPending}
                  className="absolute top-2 right-2 size-7 rounded-full bg-white/90 shadow flex items-center justify-center text-destructive hover:bg-white disabled:opacity-50"
                  aria-label="Remove video"
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Older videos */}
        {oldVideos.length > 0 && (
          <div className="px-6 py-5 border-t border-border">
            <p className={cn(metaLabelClass, "mb-3")}>Previous Videos ({oldVideos.length})</p>
            <div className="space-y-3">
              {oldVideos.map((video) => (
                <div key={video.id} className="relative rounded-xl overflow-hidden bg-black">
                  <video
                    src={`/api/v1/public/moment-requests/${publicLink}/files/${video.id}`}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full aspect-video"
                  />
                  {!isCompleted && (
                    <button
                      type="button"
                      onClick={() => removeVideo(video.id)}
                      disabled={deleteVideo.isPending}
                      className="absolute top-2 right-2 size-7 rounded-full bg-white/90 shadow flex items-center justify-center text-destructive hover:bg-white disabled:opacity-50"
                      aria-label="Remove video"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed state */}
        {isCompleted && (
          <div className="px-6 py-5 border-t border-border">
            <div className="flex items-center gap-2.5 text-success">
              <CheckCircle2 className="size-5 shrink-0" />
              <p className="text-sm font-medium">Videos submitted — thank you!</p>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-7">This request is now closed.</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            © {new Date().getFullYear()} Riqah Design
          </p>
        </div>
      </div>
    </div>
  );
}
